import React, { useState } from 'react';
import useMediaQuery from '../hooks/useMediaQuery';
import { REST_DAY_ICONS } from '../constants';

const Modal = ({ date, onAdd, onClose, existingRun, deleteRun, darkMode, types, t, distancePresets, setDistancePresets, restDayIcon }) => {
  const restIconConfig = REST_DAY_ICONS[restDayIcon] || REST_DAY_ICONS.sleep;
  const isRestDay = existingRun?.status === 'rest';
  const [dist, setDist] = useState(isRestDay ? '' : (existingRun?.distance.toString() || ''));
  const [type, setType] = useState(isRestDay ? 'easy' : (existingRun?.type || 'easy'));
  const [notes, setNotes] = useState(existingRun?.notes || '');
  const [raceName, setRaceName] = useState(existingRun?.raceName || '');
  const [showCustomizePresets, setShowCustomizePresets] = useState(false);
  const [editingPresets, setEditingPresets] = useState(distancePresets);
  const isMobile = !useMediaQuery('(min-width: 768px)');

  return (
    <div
      className={isMobile ? 'fixed inset-0 bg-black bg-opacity-60 z-50' : 'fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4'}
      onClick={onClose}
    >
      <div
        className={
          isMobile
            ? (darkMode
                ? 'fixed bottom-0 left-0 right-0 rounded-t-3xl p-6 w-full shadow-2xl bg-slate-800 text-white max-h-[90vh] overflow-y-auto animate-slide-up'
                : 'fixed bottom-0 left-0 right-0 rounded-t-3xl p-6 w-full shadow-2xl bg-white max-h-[90vh] overflow-y-auto animate-slide-up')
            : (darkMode
                ? 'rounded-2xl p-6 w-full max-w-md shadow-2xl bg-slate-800 text-white'
                : 'rounded-2xl p-6 w-full max-w-md shadow-2xl bg-white')
        }
        onClick={(e) => e.stopPropagation()}
      >
        {isMobile && (
          <div className="flex justify-center mb-4">
            <div className={darkMode ? 'w-12 h-1 rounded-full bg-slate-600' : 'w-12 h-1 rounded-full bg-slate-300'} />
          </div>
        )}
        <h2 className="text-xl font-semibold mb-2">{existingRun ? 'Edit Run' : 'Add Run'}</h2>
        <div className={darkMode ? 'text-sm mb-6 text-slate-400' : 'text-sm mb-6 text-slate-600'}>{date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</div>

        {showCustomizePresets ? (
          <div className="mb-5">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold">Customize Distance Presets</h3>
              <button
                onClick={() => setShowCustomizePresets(false)}
                className={darkMode ? 'text-sm text-slate-400 hover:text-white' : 'text-sm text-slate-600 hover:text-slate-900'}
              >
                Done
              </button>
            </div>
            <div className="space-y-3">
              {editingPresets.map((preset, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <label className="text-sm font-medium w-24">Preset {idx + 1}:</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={preset === 0 ? '' : preset}
                    onChange={(e) => {
                      const newPresets = [...editingPresets];
                      const val = e.target.value === '' ? 0 : parseFloat(e.target.value);
                      newPresets[idx] = isNaN(val) ? 0 : val;
                      setEditingPresets(newPresets);
                    }}
                    onBlur={(e) => {
                      if (e.target.value === '') {
                        const newPresets = [...editingPresets];
                        newPresets[idx] = 0;
                        setEditingPresets(newPresets);
                      }
                    }}
                    className={darkMode ? 'flex-1 p-2 rounded-xl outline-none border border-slate-600 bg-slate-700' : 'flex-1 p-2 rounded-xl outline-none border border-slate-200'}
                    placeholder="0"
                  />
                  <span className="text-sm">mi</span>
                </div>
              ))}
            </div>
            <button
              onClick={() => {
                setDistancePresets(editingPresets);
                setShowCustomizePresets(false);
              }}
              className={'w-full mt-3 py-2 rounded-xl text-white font-medium ' + t.primary + ' ' + t.hover}
            >
              Save Presets
            </button>
          </div>
        ) : (
          <div className="mb-5">
            <label className="block text-sm font-medium mb-2">Distance (miles)</label>
            <input type="number" step="0.1" value={dist} onChange={(e) => setDist(e.target.value)} className={darkMode ? 'w-full p-3 rounded-xl outline-none border border-slate-600 bg-slate-700' : 'w-full p-3 rounded-xl outline-none border border-slate-200'} placeholder="5.0" />

            <div className="mt-3 flex gap-2">
              {distancePresets.map((preset, idx) => (
                <button
                  key={idx}
                  onClick={() => setDist(preset.toString())}
                  className={darkMode ? 'flex-1 py-2 rounded-xl bg-slate-700 hover:bg-slate-600 font-medium border border-slate-600' : 'flex-1 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 font-medium border border-slate-300'}
                >
                  {preset}
                </button>
              ))}
              <button
                onClick={() => setShowCustomizePresets(true)}
                className={darkMode ? 'px-3 py-2 rounded-xl bg-slate-700 hover:bg-slate-600 text-sm border border-slate-600' : 'px-3 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-sm border border-slate-300'}
                title="Customize presets"
              >
                ⚙️
              </button>
            </div>

            {/* Mark as Rest Day */}
            <button
              onClick={() => {
                onAdd(date, { distance: 0, type: 'rest', notes: notes || 'Rest day', status: 'rest' });
              }}
              className={darkMode ? 'w-full mt-3 py-2 rounded-xl bg-slate-700 hover:bg-slate-600 font-medium border border-slate-600' : 'w-full mt-3 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 font-medium border border-slate-300'}
            >
              {restIconConfig.icon} Mark as Rest Day
            </button>
          </div>
        )}
        <div className="mb-5">
          <label className="block text-sm font-medium mb-2">Run Type</label>
          <select value={type} onChange={(e) => setType(e.target.value)} className={darkMode ? 'w-full p-3 rounded-xl outline-none border border-slate-600 bg-slate-700' : 'w-full p-3 rounded-xl outline-none border border-slate-200'}>
            {Object.entries(types).map(([k, v]) => <option key={k} value={k}>{v.name}</option>)}
          </select>
        </div>
        {type === 'race' && (
          <div className="mb-5">
            <label className="block text-sm font-medium mb-2">Race Name</label>
            <input
              type="text"
              value={raceName}
              onChange={(e) => setRaceName(e.target.value)}
              className={darkMode ? 'w-full p-3 rounded-xl outline-none border border-slate-600 bg-slate-700' : 'w-full p-3 rounded-xl outline-none border border-slate-200'}
              placeholder="e.g., Boston Marathon, Turkey Trot 5K"
            />
          </div>
        )}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-2">Notes</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} className={darkMode ? 'w-full p-3 rounded-xl outline-none border border-slate-600 bg-slate-700' : 'w-full p-3 rounded-xl outline-none border border-slate-200'} rows="3" placeholder="Optional..." />
        </div>
        <div className="flex gap-2 mb-2">
          <button
            onClick={() => {
              if (dist) {
                // If converting a rest day to a run, change status to 'planned'
                const newStatus = existingRun && existingRun.status === 'rest' ? 'planned' : (existingRun ? existingRun.status : 'planned');
                const runData = { distance: parseFloat(dist), type, notes, status: newStatus };
                if (type === 'race' && raceName) {
                  runData.raceName = raceName;
                }
                onAdd(date, runData);
              }
            }}
            className={'flex-1 text-white py-3 rounded-xl font-medium ' + t.primary + ' ' + t.hover}
          >
            {existingRun ? 'Save' : 'Add Run'}
          </button>
          {existingRun && <button onClick={() => { deleteRun(date); onClose(); }} className="px-4 py-3 rounded-xl bg-rose-500 text-white hover:bg-rose-600 font-medium">Delete</button>}
        </div>
        <button onClick={onClose} className={darkMode ? 'w-full py-3 rounded-xl font-medium bg-slate-700 hover:bg-slate-600' : 'w-full py-3 rounded-xl font-medium bg-slate-100 hover:bg-slate-200'}>Cancel</button>
      </div>
    </div>
  );
};

export default Modal;